import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AttemptResult from "@/components/practice/AttemptResult";
import ChoiceList from "@/components/practice/ChoiceList";
import PracticeEmptyState from "@/components/practice/PracticeEmptyState";
import QuestionCard from "@/components/practice/QuestionCard";
import { track } from "@/lib/analytics";
import type {
  AttemptResult as AttemptResultDto,
  PracticeQuestionForClient,
  SubmitAttemptPayload,
} from "@/lib/practice-api";
import type { LangCode } from "@roadbridge/analytics";
import type { UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/practice :: PracticeSession
 *
 * Multi-step controller for a practice question loop:
 *
 *   fetch → answer (user picks a choice) → submit → result → next
 *
 * We accept `fetchNext` + `submit` as props (matching RuleDiffPanel's
 * fetcher-prop pattern) so the surrounding page owns the API wiring
 * while this component stays testable against pure callbacks.
 *
 * Timer: `time_to_answer_ms` is the wall-clock delta between question
 * mount (the moment we transition to "answering") and the Submit click.
 * We stash `startedAtMs` in a ref so `handleSubmit` can read it without
 * re-binding on every state change. Navigating away pauses nothing —
 * consistent with the backend's "engagement time" intent for SM-2 tuning.
 */
export type FetchNextFn = (
  state: string,
) => Promise<PracticeQuestionForClient | null>;

export type SubmitFn = (
  payload: SubmitAttemptPayload,
) => Promise<AttemptResultDto>;

export interface PracticeSessionProps {
  stateCode: string;
  fetchNext: FetchNextFn;
  submit: SubmitFn;
}

type SessionState =
  | { kind: "loading" }
  | { kind: "empty" }
  | {
      kind: "answering";
      question: PracticeQuestionForClient;
      selectedChoiceId: string | null;
    }
  | {
      kind: "submitting";
      question: PracticeQuestionForClient;
      chosenChoiceId: string;
    }
  | {
      kind: "result";
      question: PracticeQuestionForClient;
      chosenChoiceId: string;
      result: AttemptResultDto;
    }
  | { kind: "error"; message: string };

export default function PracticeSession({
  stateCode,
  fetchNext,
  submit,
}: PracticeSessionProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<SessionState>({ kind: "loading" });
  // Nonce lets the Next button trigger a fresh fetch without re-mounting.
  const [nonce, setNonce] = useState(0);
  const cancelledRef = useRef(false);
  // Mutable timer for the current question — updated each time we transition
  // to "answering". Kept outside state so handleSubmit can read the latest
  // value without depending on a React render.
  const startedAtMsRef = useRef<number>(0);
  // Analytics counters — kept in refs so the `practice.session_ended`
  // cleanup can read the final totals at unmount without re-registering
  // the effect on every increment.
  const sessionStartedRef = useRef(false);
  const attemptedRef = useRef(0);
  const correctRef = useRef(0);

  useEffect(() => {
    cancelledRef.current = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const q = await fetchNext(stateCode);
        if (cancelledRef.current) return;
        if (q === null) {
          setState({ kind: "empty" });
          return;
        }
        startedAtMsRef.current = Date.now();
        setState({
          kind: "answering",
          question: q,
          selectedChoiceId: null,
        });
        // Analytics: session_started fires once per mounted session
        // (not per question advance). StrictMode-safe via the ref.
        if (!sessionStartedRef.current) {
          sessionStartedRef.current = true;
          track("practice.session_started", {
            state: stateCode as UsStateCode,
            locale: q.locale as LangCode,
          });
        }
      } catch (err) {
        if (cancelledRef.current) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [stateCode, fetchNext, nonce]);

  // Mount-once effect for the session-ended emission. Runs ONLY at real
  // component unmount (empty deps) so the cleanup reads the final refs.
  useEffect(() => {
    return () => {
      if (attemptedRef.current > 0) {
        track("practice.session_ended", {
          state: stateCode as UsStateCode,
          attempted: attemptedRef.current,
          correct: correctRef.current,
        });
      }
    };
    // stateCode intentionally omitted — we want the cleanup to read the
    // LATEST stateCode through closure at unmount, which is fine because
    // the parent remounts this component if the stateCode prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback((choiceId: string) => {
    setState((prev) => {
      if (prev.kind !== "answering") return prev;
      return { ...prev, selectedChoiceId: choiceId };
    });
  }, []);

  const handleSubmit = useCallback(() => {
    setState((prev) => {
      if (prev.kind !== "answering") return prev;
      if (!prev.selectedChoiceId) return prev;
      const question = prev.question;
      const chosenChoiceId = prev.selectedChoiceId;
      const elapsed = Math.max(0, Date.now() - startedAtMsRef.current);
      // Kick off the async submit; state transitions happen inside.
      void (async () => {
        try {
          const res = await submit({
            question_id: question.id,
            chosen_choice_id: chosenChoiceId,
            time_to_answer_ms: elapsed,
          });
          if (cancelledRef.current) return;
          setState({
            kind: "result",
            question,
            chosenChoiceId,
            result: res,
          });
          // Analytics: one answer-submitted event per server-accepted
          // attempt. `is_correct` comes from the server response (not
          // the local choice state) so optimistic UI never leaks a
          // mis-answer into the funnel.
          attemptedRef.current += 1;
          if (res.correct) correctRef.current += 1;
          track("practice.answer_submitted", {
            state: stateCode as UsStateCode,
            category: question.category,
            is_correct: res.correct,
            time_to_answer_ms: elapsed,
          });
        } catch (err) {
          if (cancelledRef.current) return;
          const message = err instanceof Error ? err.message : String(err);
          setState({ kind: "error", message });
        }
      })();
      return { kind: "submitting", question, chosenChoiceId };
    });
  }, [submit, stateCode]);

  const handleNext = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  if (state.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="practice-session-loading"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("practice.loading")}
      </div>
    );
  }

  if (state.kind === "empty") {
    return <PracticeEmptyState stateCode={stateCode} />;
  }

  if (state.kind === "error") {
    return (
      <div
        role="alert"
        data-testid="practice-session-error"
        className="mx-auto mt-xl max-w-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
      >
        {t("practice.error", { message: state.message })}
      </div>
    );
  }

  const question = state.question;
  const isAnswering = state.kind === "answering";
  const isSubmitting = state.kind === "submitting";
  const isResult = state.kind === "result";
  const selectedChoiceId = isAnswering
    ? state.selectedChoiceId
    : state.kind === "submitting" || state.kind === "result"
      ? state.chosenChoiceId
      : null;

  return (
    <div data-testid="practice-session">
      <QuestionCard question={question} />
      <ChoiceList
        choices={question.choices}
        selectedChoiceId={selectedChoiceId}
        onSelect={handleSelect}
        disabled={!isAnswering}
      />
      {!isResult && (
        <div className="mt-md">
          <button
            type="button"
            data-testid="practice-submit"
            onClick={handleSubmit}
            disabled={!isAnswering || selectedChoiceId === null || isSubmitting}
            className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? t("practice.submitting") : t("practice.submit")}
          </button>
        </div>
      )}
      {isResult && (
        <AttemptResult
          result={state.result}
          choices={question.choices}
          chosenChoiceId={state.chosenChoiceId}
          onNext={handleNext}
        />
      )}
    </div>
  );
}
